import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { SkeletonStatCard } from '../UI/SkeletonCard';
import axios from 'axios';
import { useLang } from '../../i18n/LanguageContext';

import API_BASE_URL from '../../config';
const STATUS_COLORS = {
  New: '#c9a227',
  Pending: '#a05a2c',
  Preparing: '#2563eb',
  Ready: '#228B22',
  Completed: '#16a34a',
  Cancelled: '#dc2626',
};

function AnimatedNumber({ value, prefix = '', suffix = '', decimals = 0 }) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);

  useEffect(() => {
    if (value === 0) { setDisplay(0); return; }
    const duration = 1200;
    const start = performance.now();
    const from = 0;
    const to = value;

    const step = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(from + (to - from) * eased);
      if (progress < 1) rafRef.current = requestAnimationFrame(step);
    };

    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value]);

  return (
    <span>
      {prefix}
      {decimals > 0 ? display.toFixed(decimals) : Math.round(display)}
      {suffix}
    </span>
  );
}

function StatCard({ icon, label, value, sub, color, prefix = '', suffix = '', decimals = 0, delay = 0 }) {
  return (
    <motion.div
      className="stat-card"
      style={{ '--stat-color': color }}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.45 }}
      whileHover={{ y: -4, boxShadow: '0 12px 36px rgba(160,90,44,0.18)' }}
    >
      <div className="stat-icon" style={{ background: `${color}18`, color }}>
        <i className={`bi ${icon}`}></i>
      </div>
      <div className="stat-body">
        <p className="stat-label">{label}</p>
        <h3 className="stat-value">
          <AnimatedNumber value={value} prefix={prefix} suffix={suffix} decimals={decimals} />
        </h3>
        {sub && <p className="stat-sub">{sub}</p>}
      </div>
    </motion.div>
  );
}

const RANGE_OPTIONS = [
  { label: 'This Week', value: 'week' },
  { label: 'This Month', value: 'month' },
  { label: 'This Year', value: 'year' },
];

export default function AnalyticsDashboard() {
  const { t } = useLang();
  const RANGE_LABELS = { week: t('analytics.thisWeek'), month: t('analytics.thisMonth'), year: t('analytics.thisYear') };
  const [dashboard, setDashboard] = useState(null);
  const [revenueData, setRevenueData] = useState([]);
  const [revenueRange, setRevenueRange] = useState('week');
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('ownerToken');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [dashRes, revRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/analytics/dashboard`, { headers }),
          axios.get(`${API_BASE_URL}/analytics/revenue?range=${revenueRange}`, { headers }),
        ]);
        if (dashRes.data.success) setDashboard(dashRes.data.data);
        if (revRes.data.success) setRevenueData(revRes.data.data);
      } catch (err) {
        console.error('Analytics fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revenueRange]);

  const statusPieData = dashboard
    ? Object.entries(dashboard.statusBreakdown || {}).map(([name, value]) => ({ name, value })).filter(d => d.value > 0)
    : [];

  const hourlyData = dashboard?.hourlyDistribution?.map((count, hour) => ({
    hour: `${hour.toString().padStart(2, '0')}:00`,
    orders: count,
  })).filter(d => d.orders > 0) || [];

  const topDishes = dashboard?.topDishes || [];

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h2 className="page-title"><i className="bi bi-speedometer2 me-2"></i>{t('analytics.title')}</h2>
        <p className="page-subtitle">{t('analytics.subtitle')}</p>
      </div>

      {/* Stat Cards */}
      <div className="stat-grid">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <SkeletonStatCard key={i} />)
        ) : (
          <>
            <StatCard icon="bi-currency-yen" label={t('analytics.todayRevenue')} value={dashboard?.today?.revenue || 0} prefix="¥" decimals={2} color="#228B22" delay={0} sub={`${dashboard?.today?.orders || 0} ${t('analytics.completedOrders')}`} />
            <StatCard icon="bi-receipt" label={t('analytics.totalOrders')} value={dashboard?.today?.orders || 0} color="#a05a2c" delay={0.07} sub={t('analytics.ordersToday')} />
            <StatCard icon="bi-calculator" label={t('analytics.avgOrderValue')} value={dashboard?.today?.avgOrderValue || 0} prefix="¥" decimals={2} color="#c9a227" delay={0.14} sub={t('analytics.perOrder')} />
            <StatCard icon="bi-fire" label={t('analytics.activeOrders')} value={dashboard?.today?.activeOrders || 0} color="#2563eb" delay={0.21} sub={t('analytics.pendingPrepReady')} />
          </>
        )}
      </div>

      {/* Week comparison */}
      {!loading && dashboard && (
        <motion.div
          className="week-compare-row"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {[
            { label: t('analytics.thisWeekRevenue'), val: `¥${(dashboard.weekComparison?.thisWeek?.revenue || 0).toFixed(2)}`, growth: dashboard.weekComparison?.revenueGrowth },
            { label: t('analytics.thisWeekOrders'), val: dashboard.weekComparison?.thisWeek?.orders || 0, growth: dashboard.weekComparison?.ordersGrowth },
          ].map(item => (
            <div className="compare-chip" key={item.label}>
              <span className="compare-label">{item.label}</span>
              <span className="compare-val">{item.val}</span>
              {item.growth !== undefined && (
                <span className={`compare-growth ${item.growth >= 0 ? 'positive' : 'negative'}`}>
                  <i className={`bi ${item.growth >= 0 ? 'bi-arrow-up' : 'bi-arrow-down'} me-1`}></i>
                  {Math.abs(item.growth).toFixed(1)}%
                </span>
              )}
            </div>
          ))}
        </motion.div>
      )}

      {/* Charts Row 1: Revenue + Status Pie */}
      <div className="charts-row">
        {/* Revenue Area Chart */}
        <motion.div
          className="chart-card wide"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
        >
          <div className="chart-card-header">
            <h5 className="chart-title"><i className="bi bi-graph-up me-2"></i>{t('analytics.revenueTrend')}</h5>
            <div className="range-tabs">
              {RANGE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  className={`range-tab ${revenueRange === opt.value ? 'active' : ''}`}
                  onClick={() => setRevenueRange(opt.value)}
                >
                  {RANGE_LABELS[opt.value]}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={revenueData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#a05a2c" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#a05a2c" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(160,90,44,0.1)" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#9B8579' }} />
              <YAxis tick={{ fontSize: 11, fill: '#9B8579' }} />
              <Tooltip
                contentStyle={{ background: '#fff', border: '1px solid #e8d5c4', borderRadius: 8 }}
                formatter={(v) => [`¥${v.toFixed(2)}`, 'Revenue']}
              />
              <Area type="monotone" dataKey="revenue" stroke="#a05a2c" strokeWidth={2.5} fill="url(#revenueGrad)" dot={false} activeDot={{ r: 5, fill: '#a05a2c' }} />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Status Pie */}
        <motion.div
          className="chart-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.42 }}
        >
          <div className="chart-card-header">
            <h5 className="chart-title"><i className="bi bi-pie-chart me-2"></i>{t('analytics.ordersByStatus')}</h5>
          </div>
          {statusPieData.length === 0 ? (
            <div className="chart-empty">{t('analytics.noOrdersToday')}</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={statusPieData} cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} dataKey="value">
                  {statusPieData.map((entry) => (
                    <Cell key={entry.name} fill={STATUS_COLORS[entry.name] || '#a05a2c'} />
                  ))}
                </Pie>
                <Tooltip formatter={(v, n) => [v, n]} contentStyle={{ borderRadius: 8 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </motion.div>
      </div>

      {/* Charts Row 2: Top Dishes + Hourly */}
      <div className="charts-row">
        {/* Top Dishes Bar */}
        <motion.div
          className="chart-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.48 }}
        >
          <div className="chart-card-header">
            <h5 className="chart-title"><i className="bi bi-trophy me-2"></i>{t('analytics.topDishesWeek')}</h5>
          </div>
          {topDishes.length === 0 ? (
            <div className="chart-empty">{t('analytics.noDataYet')}</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={topDishes} layout="vertical" margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(160,90,44,0.1)" />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#9B8579' }} />
                <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11, fill: '#4A3728' }} />
                <Tooltip contentStyle={{ borderRadius: 8 }} formatter={(v) => [v, 'Orders']} />
                <Bar dataKey="count" fill="#a05a2c" radius={[0, 6, 6, 0]}>
                  {topDishes.map((_, i) => (
                    <Cell key={i} fill={i === 0 ? '#228B22' : i === 1 ? '#a05a2c' : '#c9a227'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </motion.div>

        {/* Hourly Distribution */}
        <motion.div
          className="chart-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.54 }}
        >
          <div className="chart-card-header">
            <h5 className="chart-title"><i className="bi bi-clock-history me-2"></i>{t('analytics.hourlyActivity')}</h5>
          </div>
          {hourlyData.length === 0 ? (
            <div className="chart-empty">{t('analytics.noOrdersYetToday')}</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={hourlyData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(160,90,44,0.1)" />
                <XAxis dataKey="hour" tick={{ fontSize: 10, fill: '#9B8579' }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#9B8579' }} />
                <Tooltip contentStyle={{ borderRadius: 8 }} formatter={(v) => [v, 'Orders']} />
                <Bar dataKey="orders" fill="#c9a227" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </motion.div>
      </div>
    </div>
  );
}
